import React, { useEffect, useLayoutEffect, useRef } from "react";
import {Animated, Easing, Pressable, StyleSheet} from "react-native";
import {PlayerMenuKind} from "./PlayerMenuControls";
import {usePlayerMenuScrollbar} from "./usePlayerMenuScrollbar";
import {focusPreferredNode, getFocusModality, popFocusScope, pushFocusScope} from "../../tvNavigation/registry";

export function PlayerMenuShell({ menu, width, height, compact, shortLandscape, reduceMotion, onDismiss, children }: { menu: Exclude<PlayerMenuKind, null>; width: number; height: number; compact: boolean; shortLandscape: boolean; reduceMotion: boolean; onDismiss: () => void; children: React.ReactNode }) {
  const entrance = useRef(new Animated.Value(0)).current;
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const focusScope = `player.menu.${menu}`;
  usePlayerMenuScrollbar();
  useLayoutEffect(() => {
    pushFocusScope(focusScope, () => dismissRef.current());
    return () => popFocusScope(focusScope);
  }, [focusScope]);
  useEffect(() => {
    if (getFocusModality() === "pointer") return undefined;
    const frame = requestAnimationFrame(() => { focusPreferredNode(); });
    return () => cancelAnimationFrame(frame);
  }, [focusScope]);
  useEffect(() => {
    entrance.setValue(0);
    Animated.timing(entrance, { toValue: 1, duration: reduceMotion ? 1 : 140, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
  }, [entrance, menu, reduceMotion]);
  return (
    <>
      <Pressable accessibilityLabel="Dismiss player menu" focusable={false} onPress={onDismiss} style={[styles.backdrop, compact && styles.backdropCompact, shortLandscape && styles.backdropShort]} />
      <Animated.View
        accessibilityLabel="Player menu"
        testID={menu === "stats" ? "player-stats-panel" : undefined}
        onStartShouldSetResponder={() => true}
        onResponderRelease={(event) => event.stopPropagation()}
        style={[
          styles.menu,
          menu === "stats" && styles.menuStats,
          menu === "stats" && !compact && { width: Math.min(980, Math.max(280, width - 64)), maxHeight: Math.max(180, height * 0.72) },
          menu === "stats" && compact && styles.menuStatsCompact,
          menu === "language" && styles.menuLanguage,
          menu === "language" && compact && styles.menuLanguageCompact,
          menu === "versions" && styles.menuVersions,
          menu === "options" && styles.menuOptions,
          menu === "episodes" && styles.menuEpisodes,
          menu === "episodes" && !compact && { width: Math.min(620, Math.max(460, width * 0.29)), height: Math.min(580, Math.max(520, height * 0.52)) },
          shortLandscape && styles.menuShort,
          compact && menu !== "stats" && styles.menuCompact,
          { opacity: entrance, transform: [{ translateY: entrance.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) }, { scale: entrance.interpolate({ inputRange: [0, 1], outputRange: [0.985, 1] }) }] },
        ]}
      >
        {children}
      </Animated.View>
    </>
  );
}

const styles = StyleSheet.create({
  menu: { position: "absolute", zIndex: 32, right: 24, bottom: 96, width: 380, maxHeight: 430, padding: 10, borderWidth: 1, borderColor: "rgba(255,255,255,0.12)", borderRadius: 16, overflow: "hidden", backgroundColor: "rgba(8,10,14,0.94)", shadowColor: "#000", shadowOpacity: 0.48, shadowRadius: 48, shadowOffset: { width: 0, height: 16 }, elevation: 18 },
  backdrop: { position: "absolute", zIndex: 31, top: 0, left: 0, right: 0, bottom: 96 },
  backdropCompact: { bottom: 210 },
  backdropShort: { bottom: 132 },
  menuLanguage: { right: 107, bottom: 78, width: 440, maxHeight: 460 },
  menuLanguageCompact: { left: 12, right: 12, bottom: 220, width: "auto", maxHeight: 220 },
  menuVersions: { right: 154, bottom: 78, width: 720, maxHeight: 548 },
  menuOptions: { right: 402, bottom: 70 },
  menuEpisodes: { right: 24, bottom: 126, width: 560, maxHeight: 580 },
  menuStats: { top: 72, left: 32, right: "auto", bottom: "auto", width: 980, padding: 16, backgroundColor: "rgba(0,0,0,0.78)" },
  menuStatsCompact: { top: 64, left: 12, right: 12, width: "auto", maxHeight: 430 },
  menuShort: { maxHeight: 160 },
  menuCompact: { left: 12, right: 12, bottom: 220, width: "auto", maxHeight: 360 },
});
